// src/api.ts
import {
  makeAuthHeaders,
  makeUrl,
  RustyApiKeys,
  RustyConnectedAccounts,
  RustyDeposit,
  RustyReferral,
  RustySecurityLog,
  RustySubscription
} from "@gofranz/common";
import axios from "axios";
var RustyFormsApi = class {
  constructor({ baseUrl, timeout, auth, errorHandler, successHandler }) {
    this.getAuthApi = () => {
      if (!this.auth) {
        throw new Error("Auth config not set");
      }
      return this.auth;
    };
    this.getDepositApi = () => new RustyDeposit({ client: this.client });
    this.getSubscriptionApi = () => new RustySubscription({ client: this.client });
    this.getReferralApi = () => new RustyReferral({ client: this.client });
    this.getApiKeysApi = () => new RustyApiKeys({ client: this.client });
    this.getSecurityLogApi = () => new RustySecurityLog({ client: this.client });
    this.getConnectedAccountsApi = () => new RustyConnectedAccounts({ client: this.client });
    this.getAccessToken = () => this.getAuthApi().getAccessToken();
    this.getAuthHeaders = () => makeAuthHeaders(this.getAccessToken());
    this.getAxiosConfig = () => ({ headers: this.getAuthHeaders(), timeout: this.timeout });
    this.getAccountBalance = async () => this.getDepositApi().getAccountBalance(this.getAccessToken());
    this.getAccountMovements = async (query = {}) => this.getDepositApi().getAccountMovements(query, this.getAccessToken());
    this.getDeposits = async (query) => this.getDepositApi().getDeposits(query, this.getAccessToken());
    this.getDeposit = async (id) => this.getDepositApi().getDeposit(id, this.getAccessToken());
    this.newDeposit = async (deposit) => this.getDepositApi().newDeposit(deposit, this.getAccessToken());
    // Subscription endpoints
    this.getSubscriptionPlans = async () => this.getSubscriptionApi().getSubscriptionPlans();
    this.getCurrentSubscription = async () => this.getSubscriptionApi().getCurrentSubscription(this.getAccessToken());
    this.subscribeToplan = async (planId) => this.getSubscriptionApi().subscribeToplan(planId, this.getAccessToken());
    this.cancelSubscription = async () => this.getSubscriptionApi().cancelSubscription(this.getAccessToken());
    this.getSubscriptionUsage = async () => this.getSubscriptionApi().getSubscriptionUsage(this.getAccessToken());
    this.createCustomerPortalSession = async () => this.getSubscriptionApi().createCustomerPortalSession(this.getAccessToken());
    // Referral endpoints
    this.getReferralCode = async () => this.getReferralApi().getReferralCode(this.getAccessToken());
    this.getReferralStats = async () => this.getReferralApi().getReferralStats(this.getAccessToken());
    this.getReferralHistory = async () => this.getReferralApi().getReferralHistory(this.getAccessToken());
    // API Keys endpoints
    this.createApiKey = async (request) => this.getApiKeysApi().createApiKey(request, this.getAccessToken());
    this.listApiKeys = async () => this.getApiKeysApi().listApiKeys(this.getAccessToken());
    this.updateApiKey = async (id, request) => this.getApiKeysApi().updateApiKey(id, request, this.getAccessToken());
    this.deleteApiKey = async (id) => this.getApiKeysApi().deleteApiKey(id, this.getAccessToken());
    this.getApiKeyUsage = async (id) => this.getApiKeysApi().getApiKeyUsage(id, this.getAccessToken());
    // Security log (user-facing audit trail — GET /v1/a/audit)
    this.getSecurityLog = async (query = {}) => this.getSecurityLogApi().getSecurityLog(query, this.getAccessToken());
    // Connected accounts / identity linking (/v1/a/auth/*)
    this.listIdentities = async () => this.getConnectedAccountsApi().listIdentities(this.getAccessToken());
    this.linkOidcInitiate = async () => this.getConnectedAccountsApi().linkOidcInitiate(this.getAccessToken());
    this.reauthInitiate = async (purpose) => this.getConnectedAccountsApi().reauthInitiate(purpose, this.getAccessToken());
    this.reauthNostrVerify = async (purpose, signedEvent) => this.getConnectedAccountsApi().reauthNostrVerify(purpose, signedEvent, this.getAccessToken());
    this.linkCommit = async (proofToken, stepUpToken) => this.getConnectedAccountsApi().linkCommit(proofToken, stepUpToken, this.getAccessToken());
    this.unlinkIdentity = async (id, stepUpToken) => this.getConnectedAccountsApi().unlinkIdentity(id, stepUpToken, this.getAccessToken());
    // Forms endpoints
    this.newForm = async (data) => {
      const response = await this.client.post("/a/forms", data, this.getAxiosConfig());
      return response.data;
    };
    this.updateForm = async (id, data) => {
      await this.client.patch(`/a/forms/${id}`, data, this.getAxiosConfig());
    };
    this.updateFormSpecs = async (id, data) => {
      await this.client.patch(`/a/forms/${id}/specs`, data, this.getAxiosConfig());
    };
    this.deleteForm = async (id) => {
      await this.client.delete(`/a/forms/${id}`, this.getAxiosConfig());
    };
    this.getForms = async (query) => {
      const response = await this.client.get(makeUrl("/a/forms", query));
      return response.data;
    };
    this.getForm = async (id) => {
      const response = await this.client.get(`/a/forms/${id}`, this.getAxiosConfig());
      return response.data;
    };
    // Email verification endpoints
    this.newVerifiedEmail = async (data) => {
      await this.client.post("/a/emails/verify", data, this.getAxiosConfig());
    };
    this.verifyVerifiedEmail = async (id) => {
      await this.client.post("/a/emails/verify/retry", { verified_email_id: id });
    };
    this.deleteVerifiedEmail = async (id) => {
      await this.client.delete(`/a/emails/${id}`, this.getAxiosConfig());
    };
    this.setAccountEmail = async (id) => {
      await this.client.put(`/a/emails/${id}/set-account-email`, {}, this.getAxiosConfig());
    };
    this.getVerifiedEmails = async () => {
      const response = await this.client.get("/a/emails");
      return response.data;
    };
    // Form recipients endpoints
    this.newFormRecipient = async (data) => {
      await this.client.post("/a/forms/recipients", data, this.getAxiosConfig());
    };
    this.getFormRecipients = async (formId, query) => {
      const response = await this.client.get(
        makeUrl(`/a/forms/${formId}/recipients`, query)
      );
      return response.data;
    };
    this.deleteFormRecipient = async (formId, recipientId) => {
      await this.client.delete(`/a/forms/${formId}/recipients/${recipientId}`, this.getAxiosConfig());
    };
    // Messages endpoints
    this.getMessage = async (id) => {
      const response = await this.client.get(
        `/a/messages/${id}`,
        this.getAxiosConfig()
      );
      return response.data;
    };
    this.getMessages = async (query) => {
      const response = await this.client.get(makeUrl("/a/messages", query));
      return response.data;
    };
    this.updateMessage = async (id, data, notification) => {
      const config = this.getAxiosConfig();
      if (notification) {
        config.notification = notification;
      }
      await this.client.patch(`/a/messages/${id}`, data, config);
    };
    this.deleteMessage = async (id) => {
      await this.client.delete(`/a/messages/${id}`, this.getAxiosConfig());
    };
    this.getMessageView = async (messageId) => {
      const response = await this.client.get(
        `/a/messages/${messageId}/view`,
        this.getAxiosConfig()
      );
      return response.data;
    };
    this.getMessageCountByDay = async (params) => {
      const response = await this.client.get(makeUrl("/a/messages/count-by-day", params));
      return response.data;
    };
    this.getAnalyticsAggregate = async (params) => {
      const response = await this.client.get(
        makeUrl("/a/analytics/aggregate", params)
      );
      return response.data;
    };
    // ---------------------------------------------------------------------
    // Integrations (outbound-integrations engine)
    //
    // Backend: backend/formshive/src/api/integrations.rs
    // Shared frontend components live in `@gofranz/common-components/integrations`.
    // ---------------------------------------------------------------------
    this.newIntegration = async (data) => {
      const response = await this.client.post("/a/integrations", data);
      return response.data;
    };
    this.getIntegrations = async (query = {}) => {
      const response = await this.client.get(makeUrl("/a/integrations", query));
      return response.data;
    };
    this.getIntegration = async (id) => {
      const response = await this.client.get(`/a/integrations/${id}`);
      return response.data;
    };
    this.updateIntegration = async (id, data) => {
      const response = await this.client.patch(
        `/a/integrations/${id}`,
        data,
        this.getAxiosConfig()
      );
      return response.data;
    };
    this.deleteIntegration = async (id) => {
      await this.client.delete(`/a/integrations/${id}`, this.getAxiosConfig());
    };
    this.rotateIntegrationSecret = async (id) => {
      const response = await this.client.post(
        `/a/integrations/${id}/rotate-secret`,
        {},
        this.getAxiosConfig()
      );
      return response.data;
    };
    this.testIntegration = async (id, eventType) => {
      await this.client.post(
        `/a/integrations/${id}/test`,
        { event_type: eventType },
        this.getAxiosConfig()
      );
    };
    this.listIntegrationDeliveries = async (id, query = {}) => {
      const response = await this.client.get(
        makeUrl(`/a/integrations/${id}/deliveries`, query)
      );
      return response.data;
    };
    this.replayIntegrationDelivery = async (deliveryId) => {
      await this.client.post(
        `/a/integrations/deliveries/${deliveryId}/replay`,
        {},
        this.getAxiosConfig()
      );
    };
    // --- Form ⇄ integration links --------------------------------------------
    this.linkFormIntegration = async (formId, body) => {
      const response = await this.client.post(
        `/a/forms/${formId}/integrations`,
        body,
        this.getAxiosConfig()
      );
      return response.data;
    };
    this.listFormIntegrations = async (formId) => {
      const response = await this.client.get(
        `/a/forms/${formId}/integrations`
      );
      return response.data;
    };
    this.updateFormIntegration = async (formId, integrationId, body) => {
      const response = await this.client.patch(
        `/a/forms/${formId}/integrations/${integrationId}`,
        body,
        this.getAxiosConfig()
      );
      return response.data;
    };
    this.unlinkFormIntegration = async (formId, integrationId) => {
      await this.client.delete(
        `/a/forms/${formId}/integrations/${integrationId}`,
        this.getAxiosConfig()
      );
    };
    // Two-Factor Authentication endpoints
    this.getTwoFactorStatus = async () => {
      const response = await this.client.get("/a/2fa/status");
      return response.data;
    };
    this.setupTotp = async (name) => {
      const response = await this.client.post("/a/2fa/setup/totp", {
        name: name || "Authenticator"
      });
      return response.data;
    };
    this.verifyTotpSetup = async (setupToken, code) => {
      const response = await this.client.post("/a/2fa/setup/totp/verify", {
        setup_token: setupToken,
        code
      });
      return response.data;
    };
    this.disableTwoFactorMethod = async (methodId, code) => {
      await this.client.delete(`/a/2fa/methods/${methodId}`, {
        ...this.getAxiosConfig(),
        data: { code }
      });
    };
    this.getRecoveryCodesCount = async () => {
      const response = await this.client.get("/a/2fa/recovery-codes");
      return response.data;
    };
    this.regenerateRecoveryCodes = async (code) => {
      const response = await this.client.post(
        "/a/2fa/recovery-codes/regenerate",
        { code }
      );
      return response.data;
    };
    // Issues endpoints
    this.getIssues = async (params) => {
      const response = await this.client.get(makeUrl("/a/issues", params));
      return response.data;
    };
    this.dismissIssue = async (id) => {
      await this.client.patch(`/a/issues/${id}/dismiss`, {}, this.getAxiosConfig());
    };
    // Auto-response test
    this.sendAutoResponseTest = async (formId, email) => {
      await this.client.post(
        `/a/forms/${formId}/auto-response/test`,
        { email },
        this.getAxiosConfig()
      );
    };
    // Account data export & deletion
    this.exportAccountData = async () => {
      const response = await this.client.get("/a/account/export", { timeout: 12e4 });
      return response.data;
    };
    this.deleteAccount = async (confirmation, stepUpToken) => this.getConnectedAccountsApi().deleteAccount(confirmation, stepUpToken, this.getAccessToken());
    // Chat assistant (uses native fetch for SSE streaming)
    this.formChat = async (formId, message, formState, onChunk, onDone, onError) => {
      const token = this.getAccessToken();
      const url = `${this.baseUrl}/a/forms/${formId}/chat`;
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...token ? { Authorization: `Bearer ${token}` } : {}
          },
          body: JSON.stringify({ message, form_state: formState })
        });
        if (!response.ok) {
          if (response.status === 429) {
            onError("rateLimit");
            return;
          }
          onError("error");
          return;
        }
        const reader = response.body?.getReader();
        if (!reader) {
          onError("error");
          return;
        }
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";
          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed.startsWith("data: ")) continue;
            const data = trimmed.slice(6);
            if (data === "[DONE]") continue;
            try {
              const parsed = JSON.parse(data);
              const content = parsed?.choices?.[0]?.delta?.content;
              if (content) {
                onChunk(content);
              }
            } catch {
            }
          }
        }
        onDone();
      } catch {
        onError("error");
      }
    };
    if (baseUrl) {
      this.baseUrl = baseUrl;
    } else {
      this.baseUrl = "https://api.formshive.com/v1";
    }
    console.info(`[api] => baseUrl: ${this.baseUrl}`);
    if (timeout) {
      this.timeout = timeout;
    } else {
      this.timeout = 5e3;
    }
    if (auth) {
      this.auth = auth;
    } else {
      console.warn("Auth config not set");
    }
    this.errorHandler = errorHandler;
    this.successHandler = successHandler;
    this.client = axios.create({
      baseURL: this.baseUrl,
      timeout: this.timeout,
      headers: {
        "Content-Type": "application/json"
      }
    });
    this.client.interceptors.request.use(async (config) => {
      const needsAuth = config.url?.startsWith("/a/");
      if (needsAuth && this.auth) {
        if (!this.auth.hasValidAccessToken()) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
        const token = this.auth.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    });
    this.client.interceptors.response.use(
      (response) => {
        if (this.successHandler) {
          this.successHandler(response);
        }
        return response;
      },
      // Pass through successful responses
      (error) => {
        if (this.errorHandler) {
          this.errorHandler(error);
        }
        return Promise.reject(error);
      }
    );
  }
  async downloadFile(fileId) {
    const response = await fetch(makeUrl(`${this.baseUrl}/a/files/download/${fileId}`), {
      method: "GET"
    });
    if (response.ok) {
      const signedUrl = response.url;
      if (signedUrl) {
        return signedUrl;
      }
    }
    throw new Error("File not found");
  }
};

// src/types/generated.ts
var ChallengeKind = /* @__PURE__ */ ((ChallengeKind2) => {
  ChallengeKind2["ALTCHA"] = "ALTCHA";
  return ChallengeKind2;
})(ChallengeKind || {});
var CssFramework = /* @__PURE__ */ ((CssFramework2) => {
  CssFramework2["Bulma"] = "bulma";
  CssFramework2["Bootstrap"] = "bootstrap";
  CssFramework2["Formshive"] = "formshive";
  return CssFramework2;
})(CssFramework || {});
var FormRedirectOverride = /* @__PURE__ */ ((FormRedirectOverride2) => {
  FormRedirectOverride2["RedirectToSuccessHtml"] = "html";
  FormRedirectOverride2["NoRedirect"] = "none";
  return FormRedirectOverride2;
})(FormRedirectOverride || {});
var EncryptionMode = /* @__PURE__ */ ((EncryptionMode2) => {
  EncryptionMode2["None"] = "none";
  EncryptionMode2["Server"] = "server";
  EncryptionMode2["Nostr"] = "nostr";
  return EncryptionMode2;
})(EncryptionMode || {});
export {
  ChallengeKind,
  CssFramework,
  EncryptionMode,
  FormRedirectOverride,
  RustyFormsApi
};
//# sourceMappingURL=index.js.map